<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Visitor History</h5>
        <p class="page-sub">Complete log of all site visits</p>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;">
        <button class="btn-ghost" @click="importModal = true">
          <font-awesome-icon icon="file-import" /> Import CSV
        </button>
        <button class="btn-ghost" @click="openExportModal('pdf')" :disabled="exportingPdf">
          <font-awesome-icon :icon="exportingPdf ? 'spinner' : 'file-pdf'" :spin="exportingPdf" /> Export PDF
        </button>
        <button class="btn-ghost" @click="openExportModal('csv')" :disabled="exporting">
          <font-awesome-icon :icon="exporting ? 'spinner' : 'file-csv'" :spin="exporting" /> Export CSV
        </button>
        <button class="btn-danger-sm" @click="emergencyModal = true">
          <font-awesome-icon icon="bullhorn" /> Emergency Alert
        </button>
      </div>
    </div>

    <!-- Import CSV Modal -->
    <div v-if="importModal" class="modal-backdrop" @mousedown.self="importModal = false; importResult = null; importHostId = null; importHostName = ''; importHostMode = 'employee'">
      <div class="modal-box">
        <div class="modal-header">
          <h6 class="modal-title">Bulk Import Visits from CSV</h6>
          <button class="modal-close" @click="importModal = false; importResult = null; importHostId = null; importHostName = ''; importHostMode = 'employee'"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="import-info-bar">
            <div class="import-info-text">
              Upload a CSV to pre-register multiple visitors at once.
              Required: <code>first_name</code>, <code>last_name</code>, <code>purpose</code>, <code>scheduled_at</code>.
              Optional: <code>email</code>, <code>phone</code>, <code>company</code>, <code>host_name</code>, <code>note</code>.
            </div>
            <button class="btn-template" @click="downloadSampleTemplate">
              <font-awesome-icon icon="download" /> Sample Template
            </button>
          </div>
          <div class="form-row">
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:5px;">
              <label class="form-label" style="margin:0;">Host <span class="req">*</span></label>
              <div class="host-mode-toggle">
                <button type="button" :class="['host-mode-btn', importHostMode === 'employee' ? 'host-mode-btn--active' : '']"
                  @click="importHostMode = 'employee'; importHostName = ''">Employee</button>
                <button type="button" :class="['host-mode-btn', importHostMode === 'text' ? 'host-mode-btn--active' : '']"
                  @click="importHostMode = 'text'; importHostId = null">Free text</button>
              </div>
            </div>
            <search-select
              v-if="importHostMode === 'employee'"
              v-model="importHostId"
              url="payroll/employees/active"
              query-param="search"
              label-key="full_name"
              sublabel-key="employee_no"
              value-key="id"
              response-key="data"
              placeholder="— Select host —"
              search-placeholder="Type name or number…"
              :min-chars="1"
            />
            <input
              v-else
              v-model="importHostName"
              class="form-input"
              placeholder="e.g. John Smith, Finance Dept…"
            />
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">CSV File <span class="req">*</span></label>
            <input type="file" accept=".csv,text/csv" class="form-input" @change="onImportFile" />
          </div>
          <div v-if="importResult" style="margin-top:14px;">
            <div class="import-success">Imported: {{ importResult.imported }}</div>
            <div v-if="importResult.failed?.length" class="import-errors">
              <div class="import-errors-title">{{ importResult.failed.length }} rows failed:</div>
              <div v-for="f in importResult.failed" :key="f.row" class="import-error-row">Row {{ f.row }}: {{ f.error }}</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="importModal = false; importResult = null; importHostId = null; importHostName = ''; importHostMode = 'employee'">Close</button>
          <button class="btn-primary-action" @click="doImport" :disabled="importing || !importFile || (importHostMode === 'employee' ? !importHostId : !importHostName.trim())">
            <font-awesome-icon v-if="importing" icon="spinner" spin />
            {{ importing ? 'Importing…' : 'Import' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Emergency Alert Modal -->
    <div v-if="emergencyModal" class="modal-backdrop" @mousedown.self="emergencyModal = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header" style="background:#fff5f5;">
          <h6 class="modal-title" style="color:#dc2626;"><font-awesome-icon icon="bullhorn" /> Send Emergency Alert</h6>
          <button class="modal-close" @click="emergencyModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#4a5e78;margin:0 0 14px;">This will immediately email all visitors currently on site.</p>
          <div class="form-row">
            <label class="form-label">Emergency Message <span class="req">*</span></label>
            <textarea v-model="emergencyMessage" class="form-input" rows="3" style="resize:vertical;" placeholder="e.g. Please evacuate the building immediately via the nearest fire exit."></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="emergencyModal = false">Cancel</button>
          <button class="btn-danger" @click="sendEmergency" :disabled="sendingEmergency || !emergencyMessage.trim()">
            <font-awesome-icon v-if="sendingEmergency" icon="spinner" spin />
            {{ sendingEmergency ? 'Sending…' : 'Send Alert' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Public check-in URL banner -->
    <div v-if="publicCheckInUrl" class="public-url-bar">
      <div class="public-url-left">
        <font-awesome-icon icon="link" class="public-url-icon" />
        <div>
          <div class="public-url-heading">Public Visitor Check-In Link</div>
          <div class="public-url-value">{{ publicCheckInUrl }}</div>
        </div>
      </div>
      <button class="btn-copy" :class="{ 'btn-copy--copied': urlCopied }" @click="copyUrl">
        <font-awesome-icon :icon="urlCopied ? 'check' : 'copy'" />
        {{ urlCopied ? 'Copied!' : 'Copy Link' }}
      </button>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <input v-model="filters.search" class="filter-input" style="min-width:200px;" placeholder="Search visitor, company…" @input="debounceLoad" />
      <input v-model="filters.badge_no" class="filter-input" style="width:120px;" placeholder="Badge no…" @input="debounceLoad" />
      <select v-model="filters.status" class="filter-select" @change="load()">
        <option value="">All Statuses</option>
        <option value="pre_registered">Pre-Registered</option>
        <option value="checked_in">Checked In</option>
        <option value="checked_out">Checked Out</option>
        <option value="no_show">No Show</option>
        <option value="cancelled">Cancelled</option>
        <option value="pending_approval">Pending Approval</option>
        <option value="approved">Approved</option>
        <option value="denied_at_entry">Denied at Entry</option>
        <option value="auto_closed">Auto-Closed</option>
      </select>
      <select v-model="filters.visitor_type_id" class="filter-select" @change="load()">
        <option value="">All Types</option>
        <option v-for="t in visitorTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
      </select>
      <select v-model="filters.locker_id" class="filter-select" @change="load()">
        <option value="">All Lockers</option>
        <option v-for="l in lockers" :key="l.id" :value="l.id">{{ l.number }}{{ l.label ? ` · ${l.label}` : '' }}</option>
      </select>
      <input v-model="filters.from" type="date" class="filter-input" @change="load()" />
      <input v-model="filters.to"   type="date" class="filter-input" @change="load()" />
      <div class="per-page-wrap">
        <label class="per-page-label">Show</label>
        <select class="per-page-select" v-model.number="perPage" @change="load(1)">
          <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
        </select>
        <span class="per-page-label">per page</span>
      </div>
      <button v-if="hasActiveFilters" class="btn-clear-filters" @click="clearFilters">
        <font-awesome-icon icon="times" /> Clear
      </button>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-table">
      <div v-for="n in 6" :key="n" class="sk-row"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="!visits.length" class="empty-state">
      <font-awesome-icon icon="history" class="empty-icon" />
      <p>No visits found matching the filters.</p>
    </div>

    <!-- Table -->
    <div v-else class="table-card">
      <table class="data-table">
        <thead>
          <tr>
            <th>Badge</th>
            <th>Visitor</th>
            <th>Type</th>
            <th>Company</th>
            <th>Host</th>
            <th>Purpose</th>
            <th>Scheduled</th>
            <th>Checked In</th>
            <th>Checked Out</th>
            <th>Duration</th>
            <th>Locker</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="v in visits" :key="v.id">
            <td>
              <span v-if="v.badge_no" class="chip chip--indigo">{{ v.badge_no }}</span>
              <span v-if="v.badge_no && v.badge_returned === false" class="chip chip--orange" title="Badge not returned" style="margin-left:4px;">Not Returned</span>
              <span v-if="!v.badge_no" class="text-muted">—</span>
            </td>
            <td>
              <div class="vis-cell">
                <div class="vis-avatar">{{ initials(v.visitor) }}</div>
                <div>
                  <div class="vis-name">{{ v.visitor?.first_name }} {{ v.visitor?.last_name }}</div>
                  <div class="vis-sub">{{ v.visitor?.email }}</div>
                </div>
              </div>
            </td>
            <td>
              <span v-if="v.visitor_type" class="chip" :style="visitorTypeStyle(v.visitor_type)">{{ v.visitor_type.name }}</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td>{{ v.visitor?.company || '—' }}</td>
            <td>{{ hostName(v.host, v) }}</td>
            <td>{{ v.purpose }}</td>
            <td>{{ fmtDatetime(v.scheduled_at) }}</td>
            <td>{{ fmtDatetime(v.checked_in_at) }}</td>
            <td>{{ fmtDatetime(v.checked_out_at) }}</td>
            <td>{{ calcDuration(v) }}</td>
            <td>
              <span v-if="v.locker" class="chip chip--teal">{{ v.locker.number }}<span v-if="v.locker.label"> · {{ v.locker.label }}</span></span>
              <span v-else class="text-muted">—</span>
            </td>
            <td><span class="chip" :class="statusChip(v.status)">{{ fmtStatus(v.status) }}</span></td>
            <td>
              <div class="row-menu-wrap">
                <button class="row-menu-trigger" @click.stop="toggleRowMenu(v.id, $event)">
                  <font-awesome-icon icon="ellipsis-v" />
                </button>
                <transition name="menu-pop">
                  <div v-if="openMenuId === v.id" class="row-menu-dropdown"
                       :style="{ top: menuPos.top + 'px', right: menuPos.right + 'px' }"
                       @click.stop>
                    <button class="row-menu-item" @click="openDetail(v); openMenuId = null">
                      <font-awesome-icon icon="eye" class="row-menu-icon" /> View Details
                    </button>
                    <button v-if="v.badge_no" class="row-menu-item" @click="viewBadge(v); openMenuId = null">
                      <font-awesome-icon icon="id-badge" class="row-menu-icon" /> View Pass
                    </button>
                    <template v-if="v.status === 'pre_registered'">
                      <div class="row-menu-divider"></div>
                      <button class="row-menu-item row-menu-item--amber" @click="confirmAction(v, 'resend_invite'); openMenuId = null">
                        <font-awesome-icon icon="paper-plane" class="row-menu-icon" /> Resend Invite
                      </button>
                      <button class="row-menu-item row-menu-item--amber" @click="confirmAction(v, 'no_show'); openMenuId = null">
                        <font-awesome-icon icon="user-times" class="row-menu-icon" /> Mark No Show
                      </button>
                    </template>
                    <template v-if="['pre_registered','checked_in'].includes(v.status)">
                      <div class="row-menu-divider"></div>
                      <button class="row-menu-item row-menu-item--red" @click="confirmAction(v, 'cancel'); openMenuId = null">
                        <font-awesome-icon icon="times-circle" class="row-menu-icon" /> Cancel Visit
                      </button>
                    </template>
                  </div>
                </transition>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="pagination.last_page >= 1" class="pagination-bar">
        <span class="pg-info">
          Showing <strong>{{ pagination.from }}</strong>–<strong>{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong>
        </span>
        <div class="pg-controls">
          <button class="pg-btn" :disabled="pagination.current_page===1" @click="goPage(1)">«</button>
          <button class="pg-btn" :disabled="pagination.current_page===1" @click="goPage(pagination.current_page-1)">‹</button>
          <button
            v-for="p in pageRange" :key="p"
            class="pg-num" :class="p===pagination.current_page ? 'pg-num--active' : ''"
            @click="goPage(p)">{{ p }}</button>
          <button class="pg-btn" :disabled="pagination.current_page===pagination.last_page" @click="goPage(pagination.current_page+1)">›</button>
          <button class="pg-btn" :disabled="pagination.current_page===pagination.last_page" @click="goPage(pagination.last_page)">»</button>
        </div>
      </div>
    </div>
  </div>

  <!-- ── Visitor Detail modal ────────────────────────────────────────── -->
  <div v-if="detailVisit" class="vd-overlay" @click.self="detailVisit = null">
    <div class="vd-panel">

      <!-- Panel header -->
      <div class="vd-header">
        <div class="vd-header-title">Visitor Details</div>
        <button class="vd-close" @click="detailVisit = null">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </div>

      <!-- Visitor identity -->
      <div class="vd-identity">
        <div class="vd-identity-left">
          <div class="vd-avatar-lg">
            <img v-if="detailVisit.photo_url || detailVisit.visitor?.photo_url" :src="detailVisit.photo_url || detailVisit.visitor?.photo_url" class="vd-avatar-img" alt="" />
            <span v-else>{{ initials(detailVisit.visitor) }}</span>
          </div>
          <div>
            <div class="vd-full-name">{{ detailVisit.visitor?.first_name }} {{ detailVisit.visitor?.last_name }}</div>
            <div v-if="detailVisit.visitor?.company" class="vd-org">{{ detailVisit.visitor.company }}</div>
            <div class="vd-contact-row">
              <span v-if="detailVisit.visitor?.email" class="vd-contact-item">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                {{ detailVisit.visitor.email }}
              </span>
              <span v-if="detailVisit.visitor?.phone" class="vd-contact-item">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.13 9.14a19.79 19.79 0 01-3.07-8.67A2 2 0 012.07 .5H5a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.36a16 16 0 006.29 6.29l1.42-1.42a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                {{ detailVisit.visitor.phone }}
              </span>
            </div>
          </div>
        </div>
        <span class="chip vd-status-chip" :class="statusChip(detailVisit.status)">{{ fmtStatus(detailVisit.status) }}</span>
      </div>

      <!-- Details grid -->
      <div class="vd-section">
        <div class="vd-section-title">Visit Information</div>
        <div class="vd-grid">
          <div class="vd-field">
            <div class="vd-label">Host</div>
            <div class="vd-value">{{ hostName(detailVisit.host, detailVisit) }}</div>
          </div>
          <div class="vd-field">
            <div class="vd-label">Purpose</div>
            <div class="vd-value">{{ detailVisit.purpose || '—' }}</div>
          </div>
          <div class="vd-field">
            <div class="vd-label">Visitor Type</div>
            <div class="vd-value">
              <span v-if="detailVisit.visitor_type" class="chip" :style="visitorTypeStyle(detailVisit.visitor_type)">{{ detailVisit.visitor_type.name }}</span>
              <span v-else>—</span>
            </div>
          </div>
          <div class="vd-field">
            <div class="vd-label">Locker</div>
            <div class="vd-value">
              <span v-if="detailVisit.locker" class="chip chip--teal">{{ detailVisit.locker.number }}<span v-if="detailVisit.locker.label"> · {{ detailVisit.locker.label }}</span></span>
              <span v-else>—</span>
            </div>
          </div>
          <div class="vd-field">
            <div class="vd-label">Scheduled</div>
            <div class="vd-value">{{ fmtDatetime(detailVisit.scheduled_at) }}</div>
          </div>
          <div class="vd-field">
            <div class="vd-label">Location</div>
            <div class="vd-value">{{ detailVisit.location || '—' }}</div>
          </div>
          <div v-if="detailVisit.note" class="vd-field vd-field--full">
            <div class="vd-label">Note</div>
            <div class="vd-value">{{ detailVisit.note }}</div>
          </div>
          <div v-if="detailVisit.items_deposited" class="vd-field vd-field--full">
            <div class="vd-label">Items Deposited</div>
            <div class="vd-value">{{ detailVisit.items_deposited }}</div>
          </div>
        </div>
      </div>

      <!-- Timeline -->
      <div class="vd-section">
        <div class="vd-section-title">Check-In / Check-Out</div>
        <div class="vd-grid">
          <div class="vd-field">
            <div class="vd-label">Checked In</div>
            <div class="vd-value">{{ fmtDatetime(detailVisit.checked_in_at) }}</div>
          </div>
          <div class="vd-field">
            <div class="vd-label">Checked Out</div>
            <div class="vd-value">{{ fmtDatetime(detailVisit.checked_out_at) }}</div>
          </div>
          <div class="vd-field">
            <div class="vd-label">Duration</div>
            <div class="vd-value">{{ calcDuration(detailVisit) }}</div>
          </div>
          <div v-if="detailVisit.badge_no" class="vd-field">
            <div class="vd-label">Badge No.</div>
            <div class="vd-value">
              <span class="chip chip--indigo">{{ detailVisit.badge_no }}</span>
              <span v-if="detailVisit.badge_returned === true" class="chip chip--green" style="margin-left:6px;">Returned</span>
              <span v-else-if="detailVisit.badge_returned === false" class="chip chip--orange" style="margin-left:6px;">Not Returned</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="vd-footer">
        <button v-if="detailVisit.badge_no" class="btn-primary-action" @click="viewBadge(detailVisit); detailVisit = null">
          <font-awesome-icon icon="id-badge" /> View / Print Pass
        </button>
        <button v-if="detailVisit.badge_no && detailVisit.badge_returned === false" class="btn-ghost btn-ghost--green" @click="markBadgeReturned(detailVisit)" :disabled="actionLoading === detailVisit.id + '_badge'">
          <font-awesome-icon v-if="actionLoading === detailVisit.id + '_badge'" icon="spinner" spin />
          <font-awesome-icon v-else icon="check" /> Mark Badge Returned
        </button>
        <button v-if="detailVisit.status === 'pre_registered'" class="btn-ghost" @click="confirmAction(detailVisit, 'resend_invite'); detailVisit = null">
          <font-awesome-icon icon="paper-plane" /> Resend Invite
        </button>
        <button v-if="detailVisit.status === 'pre_registered'" class="btn-ghost btn-ghost--amber" @click="confirmAction(detailVisit, 'no_show'); detailVisit = null">
          <font-awesome-icon icon="user-times" /> Mark No Show
        </button>
        <button v-if="['pre_registered','checked_in'].includes(detailVisit.status)" class="btn-ghost btn-ghost--red" @click="confirmAction(detailVisit, 'cancel'); detailVisit = null">
          <font-awesome-icon icon="times-circle" /> Cancel Visit
        </button>
        <button class="btn-ghost" @click="detailVisit = null">Close</button>
      </div>

    </div>
  </div>

  <!-- ── Visitor Pass modal ──────────────────────────────────────────── -->
  <div v-if="badgeVisit" class="badge-overlay" @click.self="badgeVisit = null">
    <div class="badge-modal-wrap">

      <div class="badge-modal-actions no-print">
        <button class="btn-primary-action" @click="printBadge">
          <font-awesome-icon icon="print" /> Print Pass
        </button>
        <button class="btn-ghost" @click="badgeVisit = null">Close</button>
      </div>

      <div class="hbadge" id="hbadge-printable">
        <div class="hbadge-strip"></div>
        <div class="hbadge-content">

          <div class="hbadge-top">
            <div class="hbadge-company">
              <img v-if="company.logo" :src="company.logo" class="hbadge-logo" alt="" />
              <span class="hbadge-company-name">{{ company.name }}</span>
            </div>
            <span class="hbadge-type-label">VISITOR</span>
          </div>

          <div class="hbadge-identity">
            <div class="hbadge-photo-wrap">
              <img v-if="badgeVisit.photo_url || badgeVisit.visitor?.photo_url" :src="badgeVisit.photo_url || badgeVisit.visitor?.photo_url" class="hbadge-photo" alt="" />
              <div v-else class="hbadge-initials">{{ badgeInitials }}</div>
            </div>
            <div class="hbadge-name-block">
              <div class="hbadge-name">{{ badgeVisit.visitor?.first_name }} {{ badgeVisit.visitor?.last_name }}</div>
              <div v-if="badgeVisit.visitor?.company" class="hbadge-org">{{ badgeVisit.visitor.company }}</div>
              <div v-if="badgeVisit.visitor?.email" class="hbadge-email">{{ badgeVisit.visitor.email }}</div>
            </div>
          </div>

          <div class="hbadge-no-strip">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c0cad6" stroke-width="1.8"><rect x="3" y="4" width="2" height="16"/><rect x="7" y="4" width="1" height="16"/><rect x="10" y="4" width="3" height="16"/><rect x="15" y="4" width="1" height="16"/><rect x="18" y="4" width="3" height="16"/></svg>
            <span class="hbadge-no">{{ badgeVisit.badge_no }}</span>
          </div>

          <div class="hbadge-details">
            <div class="hbadge-detail">
              <span class="hbd-label">Visiting</span>
              <span class="hbd-value">{{ badgeVisit.host ? hostName(badgeVisit.host) : (badgeVisit.host_name || '—') }}</span>
            </div>
            <div class="hbadge-detail">
              <span class="hbd-label">Purpose</span>
              <span class="hbd-value">{{ badgeVisit.purpose }}</span>
            </div>
            <div v-if="badgeVisit.visitor_type" class="hbadge-detail">
              <span class="hbd-label">Visitor Type</span>
              <span class="hbd-value" :style="badgeVisit.visitor_type?.colour ? { color: badgeVisit.visitor_type.colour } : {}">{{ badgeVisit.visitor_type.name }}</span>
            </div>
            <div v-if="badgeVisit.locker" class="hbadge-detail">
              <span class="hbd-label">Locker</span>
              <span class="hbd-value">{{ badgeVisit.locker.number }}{{ badgeVisit.locker.label ? ` · ${badgeVisit.locker.label}` : '' }}</span>
            </div>
            <div class="hbadge-detail">
              <span class="hbd-label">Check-in time</span>
              <span class="hbd-value">{{ fmtDatetime(badgeVisit.checked_in_at) }}</span>
            </div>
          </div>

          <div class="hbadge-instruction">
            Please wear this pass visibly at all times. Present to reception when leaving.
          </div>
        </div>
      </div>

    </div>

  </div>

  <!-- ── Action Confirm Modal ─────────────────────────────────────── -->
    <transition name="fade">
      <div v-if="actionConfirm" class="modal-backdrop" @mousedown.self="actionConfirm = null">
        <div class="modal-box modal-box--sm">
          <div class="modal-header" :class="actionConfirmHeaderClass">
            <div style="display:flex;align-items:center;gap:10px;">
              <div class="confirm-icon-wrap" :class="actionConfirmIconWrapClass">
                <font-awesome-icon :icon="actionConfirmIcon" />
              </div>
              <h6 class="modal-title" :class="actionConfirmTitleClass">{{ actionConfirmTitle }}</h6>
            </div>
            <button class="modal-close" @click="actionConfirm = null"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="modal-body">
            <p class="confirm-message">{{ actionConfirmMessage }}</p>
          </div>
          <div class="modal-footer">
            <button class="btn-ghost" @click="actionConfirm = null">Cancel</button>
            <button
              :class="actionConfirmBtnClass"
              @click="executeAction"
              :disabled="!!actionLoading"
            >
              <font-awesome-icon v-if="actionLoading" icon="spinner" spin />
              {{ actionConfirmBtnLabel }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ── Export Column Picker ── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false">
              <font-awesome-icon icon="times" />
            </button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-ghost" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary" :disabled="selectedExportCols.length === 0 || exporting || exportingPdf" @click="doExport()">
              <font-awesome-icon
                :icon="(exporting || exportingPdf) ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'"
                :spin="exporting || exportingPdf" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import SearchSelect from '../../../../components/SearchSelect.vue'

export default {
  name: 'VisitorHistory',
  components: { SearchSelect },
  setup() { return { toast: useToast() } },
  data() {
    return {
      visits: [], loading: true, exporting: false, exportingPdf: false,
      filters: { search: '', badge_no: '', status: '', visitor_type_id: '', locker_id: '', from: '', to: '' },
      visitorTypes: [], lockers: [],
      perPage: 5,
      pagination: { current_page: 1, last_page: 1, total: 0, from: 0, to: 0 },
      debounceTimer: null, urlCopied: false,
      badgeVisit: null, detailVisit: null,
      actionConfirm: null, actionLoading: null, openMenuId: null, menuPos: { top: 0, right: 0 },
      company: { name: '', logo: null },
      importModal: false, importFile: null, importHostId: null, importHostName: '', importHostMode: 'employee', importing: false, importResult: null,
      emergencyModal: false, emergencyMessage: '', sendingEmergency: false,
      showExportModal:    false,
      pendingExportFmt:   'csv',
      selectedExportCols: [],
      exportColumnDefs: [
        { key: 'badge_no',       label: 'Badge',         pdfWidth: 50 },
        { key: 'visitor',        label: 'Visitor',        pdfWidth: 90 },
        { key: 'visitor_email',  label: 'Email',          pdfWidth: 110 },
        { key: 'visitor_phone',  label: 'Phone',          pdfWidth: 72 },
        { key: 'visitor_type',   label: 'Visitor Type',   pdfWidth: 72 },
        { key: 'company',        label: 'Company',        pdfWidth: 68 },
        { key: 'host',           label: 'Host',           pdfWidth: 78 },
        { key: 'purpose',        label: 'Purpose',        pdfWidth: 100 },
        { key: 'scheduled_at',   label: 'Scheduled',      pdfWidth: 72 },
        { key: 'checked_in_at',  label: 'Checked In',     pdfWidth: 72 },
        { key: 'checked_out_at', label: 'Checked Out',    pdfWidth: 72 },
        { key: 'duration',       label: 'Duration',       pdfWidth: 50 },
        { key: 'locker',         label: 'Locker',         pdfWidth: 50 },
        { key: 'status',         label: 'Status',         pdfWidth: 62 },
      ],
    }
  },
  computed: {
    hasActiveFilters() {
      const f = this.filters
      return f.search || f.badge_no || f.status || f.visitor_type_id || f.locker_id || f.from || f.to
    },
    pageRange() {
      if (!this.pagination) return [];
      const cur = this.pagination.current_page, last = this.pagination.last_page;
      const range = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
      return range;
    },
    publicCheckInUrl() {
      const code  = this.$route.params.companyCode
      const bizId = this.$store.state.getLoggedInUser?.business_id
      if (!bizId) return null
      return `${window.location.origin}/visitor-checkin/${code}/${bizId}`
    },
    badgeInitials() {
      const v = this.badgeVisit?.visitor
      return ((v?.first_name?.[0] ?? '') + (v?.last_name?.[0] ?? '')).toUpperCase()
    },
    actionConfirmTitle() {
      const t = this.actionConfirm?.type
      if (t === 'cancel')       return 'Cancel Visit'
      if (t === 'no_show')      return 'Mark as No Show'
      if (t === 'resend_invite') return 'Resend Invite'
      return ''
    },
    actionConfirmMessage() {
      const ac = this.actionConfirm
      if (!ac) return ''
      const name = `${ac.visit.visitor?.first_name ?? ''} ${ac.visit.visitor?.last_name ?? ''}`.trim()
      if (ac.type === 'cancel')        return `Cancel the visit for ${name}? This action cannot be undone.`
      if (ac.type === 'no_show')       return `Mark the visit for ${name} as No Show?`
      if (ac.type === 'resend_invite') return `Resend the invitation email to ${name}?`
      return ''
    },
    actionConfirmIcon() {
      const t = this.actionConfirm?.type
      if (t === 'cancel')        return 'times-circle'
      if (t === 'no_show')       return 'user-times'
      if (t === 'resend_invite') return 'paper-plane'
      return 'question-circle'
    },
    actionConfirmHeaderClass() {
      const t = this.actionConfirm?.type
      if (t === 'cancel')        return 'confirm-header--red'
      if (t === 'no_show')       return 'confirm-header--amber'
      if (t === 'resend_invite') return 'confirm-header--blue'
      return ''
    },
    actionConfirmIconWrapClass() {
      const t = this.actionConfirm?.type
      if (t === 'cancel')        return 'confirm-icon--red'
      if (t === 'no_show')       return 'confirm-icon--amber'
      if (t === 'resend_invite') return 'confirm-icon--blue'
      return ''
    },
    actionConfirmTitleClass() {
      const t = this.actionConfirm?.type
      if (t === 'cancel')        return 'confirm-title--red'
      if (t === 'no_show')       return 'confirm-title--amber'
      if (t === 'resend_invite') return 'confirm-title--blue'
      return ''
    },
    actionConfirmBtnClass() {
      const t = this.actionConfirm?.type
      if (t === 'cancel')        return 'btn-danger'
      if (t === 'no_show')       return 'btn-amber'
      if (t === 'resend_invite') return 'btn-primary-action'
      return 'btn-primary-action'
    },
    actionConfirmBtnLabel() {
      const t = this.actionConfirm?.type
      if (t === 'cancel')        return 'Cancel Visit'
      if (t === 'no_show')       return 'Mark No Show'
      if (t === 'resend_invite') return 'Resend Invite'
      return 'Confirm'
    },
  },
  created() {
    this.load()
    this.loadFilterOptions()
    const user    = this.$store.state.getLoggedInUser
    const baseUrl = (axios.defaults.baseURL || '').replace(/\/api\/?$/, '')
    this.company  = {
      name: user?.business_name ?? '',
      logo: user?.business_logo ? `${baseUrl}/${user.business_logo}` : null,
    }
  },
  mounted() {
    this._menuClose = () => { this.openMenuId = null }
    document.addEventListener('click', this._menuClose)
    document.addEventListener('scroll', this._menuClose, true)
  },
  beforeUnmount() {
    document.removeEventListener('click', this._menuClose)
    document.removeEventListener('scroll', this._menuClose, true)
  },
  methods: {
    clearFilters() {
      this.filters = { search: '', badge_no: '', status: '', visitor_type_id: '', locker_id: '', from: '', to: '' }
      this.load(1)
    },
    async loadFilterOptions() {
      try {
        const [typesRes, lockersRes] = await Promise.all([
          axios.get('visitors/types'),
          axios.get('visitors/lockers'),
        ])
        const rawTypes   = typesRes.data?.data ?? typesRes.data ?? []
        this.visitorTypes = Array.isArray(rawTypes) ? rawTypes : Object.values(rawTypes)
        const rawLockers  = lockersRes.data?.data ?? lockersRes.data ?? []
        this.lockers      = Array.isArray(rawLockers) ? rawLockers : Object.values(rawLockers)
      } catch (e) {
        console.warn('loadFilterOptions failed:', e?.response?.status, e?.response?.data?.message)
      }
    },
    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.publicCheckInUrl)
        this.urlCopied = true
        setTimeout(() => { this.urlCopied = false }, 2500)
      } catch { this.urlCopied = false }
    },
    confirmAction(visit, type) {
      this.actionConfirm = { visit, type }
    },
    async executeAction() {
      const { visit, type } = this.actionConfirm
      this.actionLoading = visit.id + '_' + type
      try {
        if (type === 'cancel') {
          await axios.post(`visitors/${visit.id}/cancel`)
          this.toast.success('Visit cancelled.')
        } else if (type === 'no_show') {
          await axios.post(`visitors/${visit.id}/no-show`)
          this.toast.success('Visit marked as no show.')
        } else if (type === 'resend_invite') {
          await axios.post(`visitors/${visit.id}/resend-invite`)
          this.toast.success('Invite resent to visitor.')
        }
        this.actionConfirm = null
        this.load(this.pagination.current_page)
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Action failed.')
      } finally {
        this.actionLoading = null
      }
    },
    async markBadgeReturned(visit) {
      this.actionLoading = visit.id + '_badge'
      try {
        await axios.post(`visitors/${visit.id}/badge-returned`)
        this.toast.success('Badge marked as returned.')
        if (this.detailVisit?.id === visit.id) {
          this.detailVisit = { ...this.detailVisit, badge_returned: true }
        }
        this.load(this.pagination?.current_page ?? 1)
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to update badge status.')
      } finally {
        this.actionLoading = null
      }
    },
    async load(page = 1) {
      this.loading = true
      try {
        const params = { page, per_page: this.perPage }
        if (this.filters.search)          params.search          = this.filters.search
        if (this.filters.badge_no)        params.badge_no        = this.filters.badge_no
        if (this.filters.status)          params.status          = this.filters.status
        if (this.filters.visitor_type_id) params.visitor_type_id = this.filters.visitor_type_id
        if (this.filters.locker_id)       params.locker_id       = this.filters.locker_id
        if (this.filters.from)            params.from            = this.filters.from
        if (this.filters.to)              params.to              = this.filters.to
        const r = await axios.get('visitors', { params })
        const d = r.data.data
        this.visits = d.data ?? d
        this.pagination = {
          current_page: d.current_page ?? 1,
          last_page:    d.last_page    ?? 1,
          total:        d.total        ?? 0,
          from:         d.from         ?? 0,
          to:           d.to           ?? 0,
        }
      } catch { this.toast.error('Failed to load history.') }
      finally { this.loading = false }
    },
    debounceLoad() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => this.load(), 400)
    },
    openExportModal(format) {
      this.pendingExportFmt   = format
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key)
      this.showExportModal    = true
    },

    getVisitValue(v, key) {
      switch (key) {
        case 'badge_no':       return v.badge_no || '—'
        case 'visitor':        return (`${v.visitor?.first_name ?? ''} ${v.visitor?.last_name ?? ''}`).trim() || '—'
        case 'visitor_email':  return v.visitor?.email || '—'
        case 'visitor_phone':  return v.visitor?.phone || '—'
        case 'company':        return v.visitor?.company || '—'
        case 'host':           return this.hostName(v.host, v)
        case 'purpose':        return v.purpose || '—'
        case 'scheduled_at':   return this.fmtDatetime(v.scheduled_at)
        case 'checked_in_at':  return this.fmtDatetime(v.checked_in_at)
        case 'checked_out_at': return this.fmtDatetime(v.checked_out_at)
        case 'visitor_type':   return v.visitor_type?.name || '—'
        case 'locker':         return v.locker ? (v.locker.number + (v.locker.label ? ` (${v.locker.label})` : '')) : '—'
        case 'duration':       return this.calcDuration(v)
        case 'status':         return this.fmtStatus(v.status)
        default:               return '—'
      }
    },

    async doExport() {
      if (!this.selectedExportCols.length) return
      const format = this.pendingExportFmt
      this.exporting    = format === 'csv'
      this.exportingPdf = format === 'pdf'
      this.showExportModal = false
      try {
        const params = { page: 1, per_page: 1000 }
        if (this.filters.search)          params.search          = this.filters.search
        if (this.filters.badge_no)        params.badge_no        = this.filters.badge_no
        if (this.filters.status)          params.status          = this.filters.status
        if (this.filters.visitor_type_id) params.visitor_type_id = this.filters.visitor_type_id
        if (this.filters.locker_id)       params.locker_id       = this.filters.locker_id
        if (this.filters.from)            params.from            = this.filters.from
        if (this.filters.to)              params.to              = this.filters.to

        const r    = await axios.get('visitors', { params })
        const d    = r.data.data
        const rows = d.data ?? d

        const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key))
        const headers = cols.map(c => c.label)
        const body    = rows.map(v => cols.map(c => this.getVisitValue(v, c.key)))
        const date    = new Date().toISOString().slice(0, 10)

        if (format === 'csv') {
          const csv  = [headers, ...body]
            .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
            .join('\n')
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
          const url  = URL.createObjectURL(blob)
          const a    = document.createElement('a')
          a.href = url; a.download = `visitor-history-${date}.csv`; a.click()
          URL.revokeObjectURL(url)
        } else {
          const doc      = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
          const usable   = 761
          const totalW   = cols.reduce((s, c) => s + c.pdfWidth, 0)
          const scale    = usable / totalW
          const colStyles = {}
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale } })

          doc.setFontSize(18)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(26, 43, 69)
          doc.text('Visitor History Report', 40, 46)

          doc.setFontSize(9)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(122, 144, 176)
          const parts = []
          if (this.company.name) parts.push(this.company.name)
          if (this.filters.from && this.filters.to) parts.push(`${this.filters.from} – ${this.filters.to}`)
          else if (this.filters.from) parts.push(`From ${this.filters.from}`)
          else if (this.filters.to)   parts.push(`Up to ${this.filters.to}`)
          if (this.filters.status) parts.push(this.fmtStatus(this.filters.status))
          parts.push(`Exported ${new Date().toLocaleDateString('en-GB')}`)
          doc.text(parts.join('   ·   '), 40, 62)

          doc.autoTable({
            startY: 78,
            head: [headers],
            body,
            styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
            headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: colStyles,
            tableWidth: usable,
            margin: { left: 40, right: 40 },
            didDrawPage(data) {
              doc.setFontSize(8)
              doc.setTextColor(192, 202, 214)
              doc.text(
                `Page ${data.pageNumber}`,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 16,
                { align: 'center' }
              )
            },
          })
          doc.save(`visitor-history-${date}.pdf`)
        }
      } catch {
        this.toast.error('Export failed.')
      } finally {
        this.exporting    = false
        this.exportingPdf = false
      }
    },
    openDetail(visit) {
      this.detailVisit = visit
    },
    viewBadge(visit) {
      this.badgeVisit = visit
    },
    printBadge() {
      const el = document.getElementById('hbadge-printable')
      if (!el) return
      const win = window.open('', '_blank', 'width=620,height=900')
      win.document.write(`<!DOCTYPE html><html><head><title>Visitor Pass</title><style>
        *{box-sizing:border-box;margin:0;padding:0;}
        body{background:#f4f7fb;display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
        .hbadge{width:480px;background:#fff;border-radius:20px;box-shadow:0 24px 64px rgba(0,0,0,.18);display:flex;overflow:hidden;}
        .hbadge-strip{width:8px;background:linear-gradient(180deg,#004AAD 0%,#1565d8 100%);flex-shrink:0;}
        .hbadge-content{flex:1;padding:24px 24px 20px;}
        .hbadge-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;}
        .hbadge-company{display:flex;align-items:center;gap:8px;}
        .hbadge-logo{height:24px;width:auto;object-fit:contain;background:#fff;border-radius:4px;padding:2px;}
        .hbadge-company-name{font-size:13px;font-weight:700;color:#1a2b45;}
        .hbadge-type-label{font-size:9px;font-weight:800;letter-spacing:.14em;color:#fff;background:#004AAD;padding:3px 9px;border-radius:4px;}
        .hbadge-identity{display:flex;align-items:center;gap:16px;margin-bottom:20px;}
        .hbadge-photo{width:72px;height:72px;border-radius:12px;object-fit:cover;border:2px solid #e0e7ff;flex-shrink:0;}
        .hbadge-initials{width:72px;height:72px;border-radius:12px;background:linear-gradient(135deg,#004AAD 0%,#1565d8 100%);color:#fff;font-size:24px;font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
        .hbadge-name-block{min-width:0;}
        .hbadge-name{font-size:20px;font-weight:800;color:#1a2b45;line-height:1.2;margin-bottom:2px;}
        .hbadge-org{font-size:12px;color:#7a90b0;margin-bottom:1px;}
        .hbadge-email{font-size:11px;color:#aab8cc;}
        .hbadge-no-strip{display:flex;align-items:center;gap:10px;background:#f4f7fb;border-radius:8px;padding:9px 14px;margin-bottom:16px;}
        .hbadge-no{font-size:16px;font-weight:800;color:#1a2b45;letter-spacing:.08em;}
        .hbadge-details{display:flex;flex-direction:column;border-top:1px solid #f0f4f8;margin-bottom:16px;}
        .hbadge-detail{display:flex;justify-content:space-between;align-items:baseline;padding:9px 0;border-bottom:1px solid #f0f4f8;}
        .hbadge-detail:last-child{border-bottom:none;}
        .hbd-label{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#aab8cc;}
        .hbd-value{font-size:13px;font-weight:600;color:#1a2b45;text-align:right;max-width:65%;}
        .hbadge-instruction{font-size:11px;color:#c0cad6;text-align:center;line-height:1.5;padding-top:12px;border-top:1px solid #f0f4f8;}
        @media print{body{background:#fff;min-height:auto;}.hbadge{box-shadow:none;border:1px solid #e4eaf2;}}
      </style></head><body>${el.outerHTML}</body></html>`)
      win.document.close()
      win.focus()
      setTimeout(() => { win.print(); win.close() }, 300)
    },
    goPage(p) { this.load(p) },
    calcDuration(v) {
      if (!v.checked_in_at || !v.checked_out_at) return '—'
      const mins = Math.floor((new Date(v.checked_out_at) - new Date(v.checked_in_at)) / 60000)
      if (mins < 60) return `${mins}m`
      return `${Math.floor(mins / 60)}h ${mins % 60}m`
    },
    toggleRowMenu(id, event) {
      if (this.openMenuId === id) { this.openMenuId = null; return }
      const rect = event.currentTarget.getBoundingClientRect()
      this.menuPos = { top: rect.bottom + 4, right: window.innerWidth - rect.right }
      this.openMenuId = id
    },
    visitorTypeStyle(vt) {
      if (!vt?.colour) return {}
      return { background: vt.colour + '22', color: vt.colour, border: `1px solid ${vt.colour}44` }
    },
    initials(v) { return ((v?.first_name?.[0] ?? '') + (v?.last_name?.[0] ?? '')).toUpperCase() },
    hostName(h, visit) {
      if (h) return `${h.first_name} ${h.last_name}`
      return visit?.host_name || '—'
    },
    fmtDatetime(v) {
      if (!v) return '—'
      return new Date(v).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
    },
    fmtStatus(s) {
      const map = { pre_registered: 'Pre-Registered', checked_in: 'Checked In',
        checked_out: 'Checked Out', no_show: 'No Show', cancelled: 'Cancelled' }
      return map[s] ?? s
    },
    statusChip(s) {
      return { pre_registered: 'chip--indigo', checked_in: 'chip--green', checked_out: 'chip--purple',
               no_show: 'chip--amber', cancelled: 'chip--red', pending_approval: 'chip--yellow',
               approved: 'chip--green', denied_at_entry: 'chip--red', auto_closed: 'chip--gray' }[s] ?? 'chip--gray'
    },
    downloadSampleTemplate() {
      const headers = ['first_name', 'last_name', 'email', 'phone', 'company', 'purpose', 'scheduled_at', 'host_name', 'note']
      const rows = [
        ['Jane', 'Smith', 'jane.smith@acme.com', '+44 7700 900000', 'Acme Ltd', 'Client Meeting', new Date(Date.now() + 86400000).toISOString().slice(0, 16).replace('T', ' '), 'John Doe', ''],
        ['Bob', 'Johnson', 'bob.j@supplier.com', '', 'ABC Supplies', 'Delivery', new Date(Date.now() + 172800000).toISOString().slice(0, 16).replace('T', ' '), '', 'Deliver to reception'],
        ['Alice', 'Williams', '', '', '', 'Interview', new Date(Date.now() + 259200000).toISOString().slice(0, 16).replace('T', ' '), 'Sarah Connor', ''],
      ]
      const csv = [headers, ...rows]
        .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n')
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href = url; a.download = 'visitor-import-template.csv'; a.click()
      URL.revokeObjectURL(url)
    },
    onImportFile(e) { this.importFile = e.target.files?.[0] ?? null },
    async doImport() {
      const hostOk = this.importHostMode === 'employee' ? !!this.importHostId : !!this.importHostName.trim()
      if (!this.importFile || !hostOk) return
      this.importing = true; this.importResult = null
      const form = new FormData()
      form.append('file', this.importFile)
      if (this.importHostMode === 'employee' && this.importHostId) {
        form.append('host_employee_id', this.importHostId)
      } else {
        form.append('host_name', this.importHostName)
      }
      try {
        const r = await axios.post('visitors/import/csv', form, { headers: { 'Content-Type': 'multipart/form-data' } })
        this.importResult = r.data.data ?? r.data
        this.toast.success(`Imported ${this.importResult.imported} visits.`)
        if (!this.importResult.failed?.length) this.importModal = false
        await this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Import failed.') }
      finally { this.importing = false }
    },
    async sendEmergency() {
      if (!this.emergencyMessage.trim()) return
      this.sendingEmergency = true
      try {
        const r = await axios.post('visitors/emergency/notify', { message: this.emergencyMessage })
        const n = (r.data.data ?? r.data).notified
        this.toast.success(`Emergency alert sent to ${n} visitor(s) currently on site.`)
        this.emergencyModal = false; this.emergencyMessage = ''
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to send alert.') }
      finally { this.sendingEmergency = false }
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.filter-bar { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:14px; }
.filter-input,.filter-select { height:38px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; font-family:inherit; }
.filter-input:focus,.filter-select:focus { border-color:#004AAD; }
.btn-clear-filters { display:inline-flex; align-items:center; gap:5px; background:none; border:1px solid #fca5a5; color:#dc2626; font-size:12px; font-weight:600; padding:0 12px; height:38px; border-radius:8px; cursor:pointer; font-family:inherit; white-space:nowrap; transition:background .12s; }
.btn-clear-filters:hover { background:#fff5f5; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover { background:#003b8e; }
.btn-ghost { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; cursor:pointer; font-family:inherit; }
.btn-ghost:hover:not(:disabled) { background:#e4eaf2; }
.btn-ghost:disabled { opacity:.6; cursor:not-allowed; }
.btn-icon { background:none; border:none; cursor:pointer; color:#7a90b0; padding:5px 8px; border-radius:6px; font-size:14px; }
.btn-icon:hover { background:#f0f4f8; color:#004AAD; }
.btn-icon--red:hover { background:#fef2f2; color:#dc2626; }
.btn-icon--amber:hover { background:#fffbeb; color:#d97706; }
.btn-ghost--red { color:#dc2626; border-color:#fecaca; }
.btn-ghost--red:hover { background:#fef2f2; border-color:#fca5a5; }
.btn-ghost--amber { color:#d97706; border-color:#fde68a; }
.btn-ghost--amber:hover { background:#fffbeb; border-color:#fcd34d; }
.btn-danger { display:inline-flex; align-items:center; gap:6px; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-danger:hover:not(:disabled) { background:#b91c1c; }
.btn-danger:disabled { opacity:.6; cursor:not-allowed; }
.btn-danger-sm { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#dc2626; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #fca5a5; cursor:pointer; font-family:inherit; }
.btn-danger-sm:hover { background:#fff5f5; }
.host-mode-toggle { display:flex; gap:2px; background:#f0f4f8; border-radius:7px; padding:2px; }
.host-mode-btn { background:none; border:none; font-size:12px; font-weight:600; color:#7a90b0; padding:3px 11px; border-radius:5px; cursor:pointer; font-family:inherit; transition:background .15s,color .15s; }
.host-mode-btn--active { background:#fff; color:#004AAD; box-shadow:0 1px 3px rgba(0,0,0,.1); }
.import-info-bar { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; background:#f0f6ff; border:1px solid #c7deff; border-radius:10px; padding:12px 14px; margin-bottom:16px; }
.import-info-text { font-size:12.5px; color:#3d5068; line-height:1.6; flex:1; }
.import-info-text code { background:#dbeafe; color:#1d4ed8; padding:1px 5px; border-radius:4px; font-size:11.5px; }
.btn-template { display:inline-flex; align-items:center; gap:6px; white-space:nowrap; flex-shrink:0; background:#fff; border:1px solid #93c5fd; color:#1d4ed8; font-size:12px; font-weight:600; padding:0 13px; height:34px; border-radius:8px; cursor:pointer; font-family:inherit; transition:background .15s; }
.btn-template:hover { background:#eff6ff; }
.import-success { background:#dcfce7; border:1px solid #86efac; border-radius:8px; padding:10px 14px; font-size:13px; color:#16a34a; font-weight:600; margin-bottom:8px; }
.import-errors  { background:#fff5f5; border:1px solid #fca5a5; border-radius:8px; padding:10px 14px; font-size:12px; color:#dc2626; }
.import-errors-title { font-weight:700; margin-bottom:6px; }
.import-error-row { padding:2px 0; font-family:monospace; }
.modal-backdrop { position:fixed; inset:0; background:rgba(10,20,40,.45); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
.modal-box { background:#fff; border-radius:16px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm { max-width:420px; }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 22px 14px; border-bottom:1px solid #f0f4f8; }
.modal-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close  { background:none; border:none; font-size:16px; color:#7a90b0; cursor:pointer; padding:4px; }
.modal-body   { padding:22px; flex:1; overflow-y:auto; }
.modal-footer { padding:14px 22px; border-top:1px solid #f0f4f8; display:flex; justify-content:flex-end; gap:10px; }
.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }
.req { color:#e53e3e; }
.chip--yellow { background:#fef9c3; color:#92400e; }

.table-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-table { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:11px 14px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .1s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:11px 14px; color:#1a2b45; vertical-align:middle; font-size:13px; }

.chip { display:inline-flex; align-items:center; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; white-space:nowrap; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--gray   { background:#f1f5f9; color:#64748b; }
.chip--indigo { background:#e0e7ff; color:#4338ca; }
.chip--purple { background:#ede9fe; color:#7c3aed; }
.chip--amber  { background:#fef9c3; color:#ca8a04; }
.chip--red    { background:#fce7f3; color:#be185d; }
.chip--orange { background:#fff7ed; color:#c2410c; }
.btn-ghost--green { color:#16a34a; border-color:#bbf7d0; }
.btn-ghost--green:hover { background:#f0fdf4; border-color:#86efac; }

.per-page-wrap  { display:flex; align-items:center; gap:6px; }
.per-page-label { font-size:13px; color:#7a90b0; white-space:nowrap; }
.per-page-select { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }

.pagination-bar { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; padding:14px 18px; border-top:1px solid #f0f4f8; }
.pg-info    { font-size:13px; color:#7a90b0; }
.pg-controls { display:flex; align-items:center; gap:4px; }
.pg-btn  { background:#fff; border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:14px; cursor:pointer; color:#1a2b45; transition:background .12s; }
.pg-btn:hover:not(:disabled) { background:#f4f7fb; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-num  { background:#fff; border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; cursor:pointer; color:#1a2b45; transition:background .12s; }
.pg-num:hover { background:#f4f7fb; }
.pg-num--active { background:#004AAD; color:#fff; border-color:#004AAD; font-weight:700; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:52px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.empty-state { text-align:center; padding:60px 20px; color:#aab8cc; }
.empty-icon  { font-size:3rem; margin-bottom:14px; display:block; color:#dde5f0; }

.vis-avatar { width:32px; height:32px; border-radius:50%; background:#e0e7ff; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; color:#4338ca; flex-shrink:0; }
.vis-cell   { display:flex; align-items:center; gap:8px; }
.vis-name   { font-weight:600; font-size:13px; color:#1a2b45; }
.vis-sub    { font-size:11px; color:#7a90b0; }
.text-muted { color:#7a90b0; font-size:12px; }

.public-url-bar { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:14px 18px; margin-bottom:16px; }
.public-url-left { display:flex; align-items:center; gap:12px; min-width:0; }
.public-url-icon { color:#004AAD; font-size:14px; flex-shrink:0; }
.public-url-heading { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-bottom:2px; }
.public-url-value { font-size:13px; font-weight:600; color:#1a2b45; word-break:break-all; }
.btn-copy { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:12px; font-weight:600; padding:0 14px; height:34px; border-radius:8px; cursor:pointer; font-family:inherit; white-space:nowrap; flex-shrink:0; transition:background .15s,color .15s; }
.btn-copy:hover { background:#e4eaf2; }
.btn-copy--copied { background:#dcfce7; color:#16a34a; border-color:#bbf7d0; }

/* ── Badge modal overlay ──────────────────────────────────────────────────── */
.badge-overlay { position:fixed; inset:0; background:rgba(0,0,0,.55); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; }
.badge-modal-wrap { display:flex; flex-direction:column; align-items:center; gap:16px; width:100%; max-width:500px; }
.badge-modal-actions { display:flex; gap:10px; }

/* ── Badge card (matches vc-badge on public page) ────────────────────────── */
.hbadge { width:100%; background:#fff; border-radius:20px; box-shadow:0 24px 64px rgba(0,0,0,.22); display:flex; overflow:hidden; }
.hbadge-strip { width:8px; background:linear-gradient(180deg,#004AAD 0%,#1565d8 100%); flex-shrink:0; }
.hbadge-content { flex:1; padding:24px 24px 20px; }

.hbadge-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:20px; }
.hbadge-company { display:flex; align-items:center; gap:8px; }
.hbadge-logo { height:24px; width:auto; object-fit:contain; background:#fff; border-radius:4px; padding:2px; }
.hbadge-company-name { font-size:13px; font-weight:700; color:#1a2b45; }
.hbadge-type-label { font-size:9px; font-weight:800; letter-spacing:.14em; color:#fff; background:#004AAD; padding:3px 9px; border-radius:4px; }

.hbadge-identity { display:flex; align-items:center; gap:16px; margin-bottom:20px; }
.hbadge-photo { width:72px; height:72px; border-radius:12px; object-fit:cover; border:2px solid #e0e7ff; flex-shrink:0; }
.hbadge-initials { width:72px; height:72px; border-radius:12px; background:linear-gradient(135deg,#004AAD 0%,#1565d8 100%); color:#fff; font-size:24px; font-weight:800; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.hbadge-name-block { min-width:0; }
.hbadge-name  { font-size:20px; font-weight:800; color:#1a2b45; line-height:1.2; margin-bottom:2px; }
.hbadge-org   { font-size:12px; color:#7a90b0; margin-bottom:1px; }
.hbadge-email { font-size:11px; color:#aab8cc; }

.hbadge-no-strip { display:flex; align-items:center; gap:10px; background:#f4f7fb; border-radius:8px; padding:9px 14px; margin-bottom:16px; }
.hbadge-no { font-size:16px; font-weight:800; color:#1a2b45; letter-spacing:.08em; }

.hbadge-details { display:flex; flex-direction:column; border-top:1px solid #f0f4f8; margin-bottom:16px; }
.hbadge-detail { display:flex; justify-content:space-between; align-items:baseline; padding:9px 0; border-bottom:1px solid #f0f4f8; }
.hbadge-detail:last-child { border-bottom:none; }
.hbd-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#aab8cc; }
.hbd-value { font-size:13px; font-weight:600; color:#1a2b45; text-align:right; max-width:65%; }

.hbadge-instruction { font-size:11px; color:#c0cad6; text-align:center; line-height:1.5; padding-top:12px; border-top:1px solid #f0f4f8; }

/* ── Visitor Detail modal ─────────────────────────────────────────────────── */
.vd-overlay { position:fixed; inset:0; background:rgba(15,23,42,.5); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; }
.vd-panel {
  background:#fff; border-radius:16px; width:100%; max-width:560px;
  max-height:90vh; overflow-y:auto; display:flex; flex-direction:column;
  box-shadow:0 24px 64px rgba(0,0,0,.22);
}

/* Header */
.vd-header { display:flex; align-items:center; justify-content:space-between; padding:20px 24px 16px; border-bottom:1px solid #f0f4f8; flex-shrink:0; }
.vd-header-title { font-size:16px; font-weight:700; color:#1a2b45; }
.vd-close { background:none; border:none; cursor:pointer; color:#aab8cc; padding:4px; border-radius:6px; display:flex; align-items:center; }
.vd-close:hover { background:#f0f4f8; color:#3d5068; }

/* Identity strip */
.vd-identity { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; padding:20px 24px; border-bottom:1px solid #f0f4f8; }
.vd-identity-left { display:flex; align-items:flex-start; gap:14px; flex:1; min-width:0; }
.vd-avatar-lg {
  width:56px; height:56px; border-radius:14px; flex-shrink:0;
  background:linear-gradient(135deg,#004AAD 0%,#1565d8 100%);
  color:#fff; font-size:20px; font-weight:800;
  display:flex; align-items:center; justify-content:center; overflow:hidden;
}
.vd-avatar-img { width:100%; height:100%; object-fit:cover; }
.vd-full-name { font-size:17px; font-weight:800; color:#1a2b45; line-height:1.2; margin-bottom:3px; }
.vd-org { font-size:13px; color:#7a90b0; margin-bottom:6px; }
.vd-contact-row { display:flex; flex-wrap:wrap; gap:10px; }
.vd-contact-item { display:inline-flex; align-items:center; gap:5px; font-size:12px; color:#4a5e78; }
.vd-status-chip { flex-shrink:0; align-self:flex-start; }

/* Sections */
.vd-section { padding:18px 24px; border-bottom:1px solid #f0f4f8; }
.vd-section-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#aab8cc; margin-bottom:14px; }
.vd-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px 20px; }
.vd-field { display:flex; flex-direction:column; gap:3px; }
.vd-field--full { grid-column:1 / -1; }
.vd-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#aab8cc; }
.vd-value { font-size:13px; font-weight:600; color:#1a2b45; line-height:1.5; }

/* Footer */
.vd-footer { display:flex; gap:10px; padding:16px 24px; flex-shrink:0; }

/* -- Column picker modal ------------------------------------------------- */
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.btn-primary { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-primary:hover:not(:disabled) { background:#003b8e; }
.btn-primary:disabled { opacity:.45; cursor:not-allowed; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }

.chip--teal { background:#ccfbf1; color:#0f766e; }

/* ── Confirm modal ─────────────────────────────────────────────────── */
.confirm-header--red   { background:#fff5f5; }
.confirm-header--amber { background:#fffbeb; }
.confirm-header--blue  { background:#eff6ff; }
.confirm-icon-wrap { width:36px; height:36px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.confirm-icon--red   { background:#fee2e2; color:#dc2626; }
.confirm-icon--amber { background:#fef3c7; color:#d97706; }
.confirm-icon--blue  { background:#dbeafe; color:#2563eb; }
.confirm-title--red   { color:#dc2626 !important; }
.confirm-title--amber { color:#d97706 !important; }
.confirm-title--blue  { color:#2563eb !important; }
.confirm-message { margin:0; font-size:13px; color:#4a5e78; line-height:1.6; }
.btn-amber { display:inline-flex; align-items:center; gap:6px; background:#d97706; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-amber:hover:not(:disabled) { background:#b45309; }
.btn-amber:disabled { opacity:.6; cursor:not-allowed; }

/* ── Row action dropdown ─────────────────────────────────────────── */
.row-menu-wrap { position:relative; display:inline-block; }
.row-menu-trigger { background:none; border:none; cursor:pointer; color:#aab8cc; padding:5px 8px; border-radius:6px; font-size:15px; line-height:1; transition:background .1s,color .1s; }
.row-menu-trigger:hover { background:#f0f4f8; color:#3d5068; }
.row-menu-dropdown {
  position:fixed; z-index:9999;
  background:#fff; border:1px solid #e4eaf2; border-radius:10px;
  box-shadow:0 8px 24px rgba(0,0,0,.12); min-width:170px; overflow:hidden;
  padding:4px 0;
}
.row-menu-item {
  display:flex; align-items:center; gap:9px; width:100%; padding:9px 14px;
  background:none; border:none; cursor:pointer; font-size:13px; font-weight:500;
  color:#1a2b45; font-family:inherit; text-align:left; transition:background .1s;
  white-space:nowrap;
}
.row-menu-item:hover { background:#f4f7fb; }
.row-menu-item--amber { color:#d97706; }
.row-menu-item--amber:hover { background:#fffbeb; }
.row-menu-item--red { color:#dc2626; }
.row-menu-item--red:hover { background:#fff5f5; }
.row-menu-icon { width:14px; opacity:.75; flex-shrink:0; }
.row-menu-divider { height:1px; background:#f0f4f8; margin:4px 0; }
.menu-pop-enter-active { transition:opacity .1s ease, transform .1s ease; }
.menu-pop-leave-active { transition:opacity .08s ease, transform .08s ease; }
.menu-pop-enter-from, .menu-pop-leave-to { opacity:0; transform:translateY(-4px) scale(.97); }
</style>
